import { jwtDecode } from 'jwt-decode'
import { captureEvent } from 'src/common/sentry-client'
import { readStoredLocaleRaw } from 'src/core/locale/runtime'
import { clearRegistrationSiteInfo, readRegistrationSiteInfo } from 'src/services/registration-site-info-snapshot.js'

const EVENT_MESSAGE = 'registration.site_info_snapshot'
const CURRENCY_SELECTOR_ANOMALY_EVENT_MESSAGE = 'registration.currency_selector_anomaly'

export const REGISTRATION_SOURCES = Object.freeze({
  MODAL: 'modal',
  LANDING: 'landing',
  SOCIAL: 'social',
  REGION_MODAL: 'region_modal',
})

function getPhoneCountry(value) {
  if (typeof value === 'string') {
    return value || null
  }

  return value?.iso2 ?? null
}

function getUserId(token) {
  if (!token) {
    return null
  }

  try {
    return jwtDecode(token)?.user_id ?? null
  } catch {
    return null
  }
}

function getStoredLocale() {
  try {
    return readStoredLocaleRaw() || null
  } catch {
    return null
  }
}

function getBrowserLanguages() {
  if (typeof navigator === 'undefined') {
    return []
  }

  if (Array.isArray(navigator.languages) && navigator.languages.length) {
    return [...navigator.languages]
  }

  return navigator.language ? [navigator.language] : []
}

function compactTags(tags) {
  return Object.fromEntries(Object.entries(tags).filter(([, value]) => value !== null && value !== undefined))
}

function getCurrencyCodes(currencies) {
  if (!Array.isArray(currencies)) {
    return []
  }

  return currencies.map((currency) => currency?.code).filter(Boolean)
}

function getSiteInfoRequestErrorContext(error) {
  const errorName = typeof error?.name === 'string' ? error.name : 'unknown'

  if (typeof error?.status === 'number') {
    return { errorKind: 'http_error', errorName }
  }

  const errorKindsByName = {
    AbortError: 'request_aborted',
    Error: 'graphql_or_response_error',
    SyntaxError: 'invalid_response',
    TypeError: 'network_error',
  }

  return {
    errorKind: errorKindsByName[errorName] ?? 'unknown',
    errorName,
  }
}

function getSiteInfoRequestNetworkContext(data) {
  const context = data.siteInfoRequestNetworkContext ?? data.siteInfoRequestError?.siteInfoRequestNetworkContext
  const toBooleanTag = (value) => (typeof value === 'boolean' ? String(value) : 'unknown')

  return {
    apiIsCrossOrigin: toBooleanTag(context?.apiIsCrossOrigin),
    cspBlocked: toBooleanTag(context?.cspBlocked),
    isOnline: toBooleanTag(context?.isOnline),
    resourceTimingAvailable: toBooleanTag(context?.resourceTimingAvailable),
    responseTimingStarted: toBooleanTag(context?.responseTimingStarted),
  }
}

function normalizeFrontendState(locale, frontendState = {}) {
  return {
    locale: locale ?? null,
    phoneCountry: getPhoneCountry(frontendState.phoneCountry),
    phoneDerivedCurrency: frontendState.phoneDerivedCurrency ?? null,
    registrationCurrency: frontendState.registrationCurrency ?? null,
    registrationRegion: frontendState.registrationRegion ?? null,
  }
}

export function captureRegistrationSiteInfo({
  token,
  source,
  locale = null,
  frontendState = {},
  initialFrontendState = null,
} = {}) {
  try {
    const snapshot = readRegistrationSiteInfo()
    if (!snapshot) {
      return
    }

    const userId = getUserId(token)
    const normalizedFrontendState = normalizeFrontendState(locale, frontendState)
    const normalizedInitialFrontendState = initialFrontendState
      ? normalizeFrontendState(initialFrontendState.locale, initialFrontendState)
      : null

    captureEvent({
      message: EVENT_MESSAGE,
      level: 'info',
      ...(userId === null ? {} : { user: { id: String(userId) } }),
      tags: compactTags({
        registration_source: source,
        site_region: snapshot.siteInfo.region,
        site_real_region: snapshot.siteInfo.realRegion,
        site_default_currency: snapshot.defaultCurrency?.code,
        registration_currency: normalizedFrontendState.registrationCurrency,
        phone_country: normalizedFrontendState.phoneCountry,
        frontend_locale: normalizedFrontendState.locale,
        ...(userId === null ? { user_id_missing: true } : {}),
      }),
      extra: {
        siteInfo: snapshot.siteInfo,
        currencies: snapshot.currencies,
        defaultCurrency: snapshot.defaultCurrency,
        initialFrontendState: normalizedInitialFrontendState,
        frontendState: {
          ...normalizedFrontendState,
          storedLocale: getStoredLocale(),
          browserLanguages: getBrowserLanguages(),
        },
      },
    })
  } catch {
    // Observability must never interrupt a successful registration.
  } finally {
    clearRegistrationSiteInfo()
  }
}

export function captureRegistrationCurrencySelectorAnomaly({ reason, data = {} } = {}) {
  try {
    const currencies = data.currencies
    const currencyCodes = getCurrencyCodes(currencies)
    const siteInfo = data.siteInfo
    const siteInfoRequestError = getSiteInfoRequestErrorContext(data.siteInfoRequestError)
    const siteInfoRequestNetworkContext = getSiteInfoRequestNetworkContext(data)

    captureEvent({
      message: CURRENCY_SELECTOR_ANOMALY_EVENT_MESSAGE,
      level: 'warning',
      tags: compactTags({
        currency_selector_reason: reason,
        currency_count: Array.isArray(currencies) ? String(currencies.length) : 'not_array',
        is_bot: data.isBot ? 'true' : 'false',
        site_info_present: siteInfo ? 'true' : 'false',
        site_info_request_error_kind: siteInfoRequestError.errorKind,
        site_info_request_error_name: siteInfoRequestError.errorName,
        site_info_request_api_is_cross_origin: siteInfoRequestNetworkContext.apiIsCrossOrigin,
        site_info_request_csp_blocked: siteInfoRequestNetworkContext.cspBlocked,
        site_info_request_is_online: siteInfoRequestNetworkContext.isOnline,
        site_info_request_resource_timing_available: siteInfoRequestNetworkContext.resourceTimingAvailable,
        site_info_request_response_timing_started: siteInfoRequestNetworkContext.responseTimingStarted,
      }),
      extra: {
        currencyCodes,
        defaultCurrencyCode: data.defaultCurrency?.code ?? null,
        siteInfo: siteInfo
          ? {
              domain: siteInfo.domain ?? null,
              region: siteInfo.region ?? null,
              realRegion: siteInfo.realRegion ?? null,
            }
          : null,
        siteInfoRequestStatus: data.siteInfoRequestStatus ?? null,
      },
    })
  } catch {
    // Observability must never interrupt application bootstrap.
  }
}

export {
  clearPersistedRegistrationSiteInfo,
  clearRegistrationSiteInfo,
  persistRegistrationSiteInfo,
  rememberRegistrationSiteInfo,
} from 'src/services/registration-site-info-snapshot.js'
